import * as fx from '../audio/fx';
import { app, commit, on } from '../app/state';
import { CLAMPS } from '../core/constants';
import { firstOpen, move, openTasks, pomodorosLeft } from '../core/tasks';
import type { Task } from '../core/types';
import { clamp, pad, uid } from '../core/util';
import { trackTask } from '../platform/analytics';
import { announce } from './announcer';
import { $, animate, holdable, ic } from './dom';
import { toast } from './toast';

const list = $('#taskList');
const input = $<HTMLInputElement>('#taskInput');
let estPick = 1;
let suppressRowClick = false;
let editingId: string | null = null;
let editorEl: HTMLLIElement | null = null;
let dragging = false;
let renderPending = false;

const tasks = () => app.data.tasks;
const save = () => commit(['tasks']);
/** The live task for an id — closures may hold an object that a cross-tab sync has since replaced. */
const taskById = (id: string) => tasks().find((x) => x.id === id);

function setActive(id: string | null) {
  app.data.activeTaskId = id;
}

/** Row controls that keep keyboard focus across re-renders. */
const CONTROLS = ['t-body', 't-check', 't-edit', 't-grip', 't-del'] as const;

function focusSnapshot() {
  const el = document.activeElement;
  if (!(el instanceof HTMLElement) || !list.contains(el)) return null;
  const row = el.closest<HTMLElement>('li.task');
  return {
    el,
    id: row?.dataset.id,
    index: row ? [...list.children].indexOf(row) : -1,
    control: CONTROLS.find((c) => el.classList.contains(c)),
  };
}

/** Puts focus back on the same control, or the same control on the row that took its place. */
function restoreFocus(snap: NonNullable<ReturnType<typeof focusSnapshot>>) {
  if (snap.el.isConnected) {
    if (document.activeElement !== snap.el) snap.el.focus({ preventScroll: true });
    return;
  }
  const rows = [...list.children] as HTMLElement[];
  const row = rows.find((r) => r.dataset.id === snap.id) ?? rows[Math.min(snap.index, rows.length - 1)];
  const same = snap.control ? row?.querySelector<HTMLElement>(`.${snap.control}`) : null;
  const target = (same && same.tabIndex >= 0 ? same : row?.querySelector<HTMLElement>('button')) ?? input;
  target.focus({ preventScroll: true });
}

export function renderTasks() {
  // Rebuilding mid-drag would detach the dragged row; catch up when the drag ends.
  if (dragging) return void (renderPending = true);
  const d = app.data;
  const snap = focusSnapshot();
  if (editingId && !d.tasks.some((t) => t.id === editingId)) editingId = null;
  const rows = d.tasks.map((t, i) => {
    if (t.id !== editingId) return taskRow(t, i);
    // An open editor is kept as-is, so a phase ending or another tab's change never wipes typing.
    if (editorEl?.dataset.id !== t.id) editorEl = editorRow(t);
    return editorEl;
  });
  if (!editingId) editorEl = null;
  for (const c of [...list.children]) if (!rows.includes(c as HTMLLIElement)) c.remove();
  rows.forEach((r, i) => {
    if (list.children[i] !== r) list.insertBefore(r, list.children[i] ?? null);
  });
  if (snap) restoreFocus(snap);
  $('#taskEmpty').hidden = d.tasks.length > 0;
  const open = openTasks(d.tasks).length;
  const poms = pomodorosLeft(d.tasks);
  const sum = $('#taskSum');
  if (d.tasks.length) {
    sum.innerHTML = `<b>${open}</b> OPEN · <b>${poms}</b> POMODORO${poms === 1 ? '' : 'S'} LEFT`;
  } else sum.textContent = 'NO TASKS YET';
  const cnt = $('#tCount');
  cnt.textContent = open > 9 ? '9+' : String(open);
  cnt.classList.toggle('zero', open === 0);
  cnt.setAttribute('aria-label', `${open} open task${open === 1 ? '' : 's'}`);
  $('#clearDone').hidden = !d.tasks.some((t) => t.done);
}

function taskRow(t: Task, i: number): HTMLLIElement {
  const active = t.id === app.data.activeTaskId;
  const pct = Math.round((t.count / t.est) * 100);
  const status = t.done
    ? '<span class="t-status done">DONE</span>'
    : active
      ? '<span class="t-status now">NOW</span>'
      : `<span class="t-status q">${pad(i + 1)}</span>`;
  let dots = '';
  for (let j = 0; j < t.est; j++) dots += `<i class="p${j < t.count ? ' f' : ''}"></i>`;
  const eta = t.done ? 'COMPLETE' : `≈ ${Math.max(0, t.est - t.count) * app.data.settings.focus} MIN LEFT`;

  const li = document.createElement('li');
  li.className = `task${t.done ? ' done' : ''}${active ? ' active' : ''}`;
  li.dataset.id = t.id;
  li.innerHTML = `<button class="t-check" type="button" aria-pressed="${t.done}">${t.done ? ic('check') : ''}</button>
    <div class="t-body">
      <div class="t-top"><span class="t-text"></span>${status}</div>
      <p class="t-notes" hidden></p>
      <div class="t-track" aria-hidden="true"><i></i></div>
      <div class="t-meta"><span class="t-dots" aria-hidden="true">${dots}</span><b>${t.count}/${t.est}</b><span class="t-eta">${eta}</span></div>
    </div>
    <div class="t-acts">
      <button class="t-edit" type="button">${ic('edit')}</button>
      <button class="t-grip" type="button" title="Drag to reorder">${ic('grip')}</button>
      <button class="t-del" type="button">${ic('x')}</button>
    </div>`;
  li.querySelector<HTMLElement>('.t-track i')!.style.width = `${t.done ? 100 : pct}%`;
  li.querySelector('.t-text')!.textContent = t.text;
  if (t.notes) {
    const n = li.querySelector<HTMLElement>('.t-notes')!;
    n.textContent = t.notes;
    n.hidden = false;
  }
  const label = (verb: string) => `${verb} “${t.text}”`;
  const body = li.querySelector<HTMLElement>('.t-body')!;
  if (!t.done) {
    // Keyboard path to "work on this task" (the row itself is only clickable with a pointer).
    body.setAttribute('role', 'button');
    body.tabIndex = 0;
    body.setAttribute('aria-pressed', String(active));
    body.setAttribute('aria-label', `Work on “${t.text}”`);
    body.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      select();
    });
  }
  li.querySelector('.t-check')!.setAttribute('aria-label', label(t.done ? 'Reopen' : 'Complete'));
  li.querySelector('.t-edit')!.setAttribute('aria-label', label('Edit'));
  li.querySelector('.t-grip')!.setAttribute('aria-label', `${label('Reorder')} — use arrow keys`);
  li.querySelector('.t-del')!.setAttribute('aria-label', label('Delete'));

  li.querySelector('.t-check')!.addEventListener('click', (ev) => {
    ev.stopPropagation();
    toggleTask(t);
  });
  li.querySelector('.t-del')!.addEventListener('click', (ev) => {
    ev.stopPropagation();
    deleteTask(t);
  });
  li.querySelector('.t-edit')!.addEventListener('click', (ev) => {
    ev.stopPropagation();
    editingId = t.id;
    renderTasks();
    list.querySelector<HTMLInputElement>('.te-text')?.focus();
  });
  attachGrip(li, t);
  const select = () => {
    if (suppressRowClick || t.done || app.data.activeTaskId === t.id) return;
    setActive(t.id);
    fx.tap();
    save();
    announce(`Now working on ${t.text}`);
  };
  li.addEventListener('click', select);
  return li;
}

function editorRow(t: Task): HTMLLIElement {
  const li = document.createElement('li');
  li.className = 'task editing';
  li.dataset.id = t.id;
  let est = t.est;
  li.innerHTML = `<form class="t-editor">
      <label class="sr-only">Task</label>
      <input class="te-text" maxlength="200" required autocomplete="off">
      <label class="sr-only">Notes</label>
      <textarea class="te-notes" maxlength="2000" rows="2" placeholder="Notes (optional)"></textarea>
      <div class="task-add-row">
        <div class="est-pick" role="group" aria-label="Estimated pomodoros"><span aria-hidden="true">POMODOROS</span>
          <button class="pm" type="button" data-d="-1" aria-label="Fewer pomodoros">−</button><b aria-live="polite"></b>
          <button class="pm" type="button" data-d="1" aria-label="More pomodoros">+</button></div>
        <div class="te-btns"><button class="mini" type="button" data-cancel>CANCEL</button>
        <button class="mini primary" type="submit">SAVE</button></div>
      </div>
    </form>`;
  const text = li.querySelector<HTMLInputElement>('.te-text')!;
  const notes = li.querySelector<HTMLTextAreaElement>('.te-notes')!;
  // Ids are set through the DOM, never interpolated into markup.
  text.id = `te-${t.id}`;
  notes.id = `tn-${t.id}`;
  const [textLabel, notesLabel] = li.querySelectorAll('label');
  textLabel!.htmlFor = text.id;
  notesLabel!.htmlFor = notes.id;
  const estEl = li.querySelector('b')!;
  text.value = t.text;
  notes.value = t.notes;
  estEl.textContent = String(est);
  li.querySelectorAll<HTMLButtonElement>('.pm').forEach((b) =>
    holdable(b, () => {
      const v = clamp(est + Number(b.dataset.d), CLAMPS.est[0], CLAMPS.est[1]);
      if (v !== est) {
        est = v;
        estEl.textContent = String(est);
        fx.tick();
      }
    }),
  );
  const close = () => {
    editingId = null;
    renderTasks();
    list.querySelector<HTMLElement>(`[data-id="${CSS.escape(t.id)}"] .t-edit`)?.focus();
  };
  li.querySelector('[data-cancel]')!.addEventListener('click', close);
  li.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
    }
  });
  li.querySelector('form')!.addEventListener('submit', (e) => {
    e.preventDefault();
    const v = text.value.trim();
    if (!v) return text.focus();
    const cur = taskById(t.id);
    if (!cur) return close(); // deleted in another tab meanwhile
    cur.text = v;
    cur.notes = notes.value.trim();
    cur.est = est;
    if (cur.count >= cur.est && !cur.done) {
      cur.done = true;
      if (app.data.activeTaskId === cur.id) setActive(firstOpen(tasks()));
    }
    editingId = null;
    fx.tap();
    save();
    list.querySelector<HTMLElement>(`[data-id="${CSS.escape(cur.id)}"] .t-edit`)?.focus();
  });
  return li;
}

/** After UNDO from a toast, move keyboard focus back into the list instead of the vanishing toast. */
function focusRowAfterUndo(id: string) {
  if (!$('#toasts').contains(document.activeElement)) return;
  list.querySelector<HTMLElement>(`[data-id="${CSS.escape(id)}"] .t-check`)?.focus();
}

function toggleTask(t: Task) {
  t.done = !t.done;
  if (t.done) trackTask('done');
  if (t.done && app.data.activeTaskId === t.id) setActive(firstOpen(tasks()));
  if (!t.done && !app.data.activeTaskId) setActive(t.id);
  fx.tap();
  save();
}

function deleteTask(t: Task) {
  const idx = tasks().indexOf(t);
  const wasActive = app.data.activeTaskId === t.id;
  app.data.tasks = tasks().filter((x) => x.id !== t.id);
  if (wasActive) setActive(firstOpen(tasks()));
  save();
  toast(`Deleted “${t.text.length > 28 ? t.text.slice(0, 27) + '…' : t.text}”`, {
    action: {
      label: 'UNDO',
      run: () => {
        if (taskById(t.id)) return; // already back (e.g. via another tab)
        app.data.tasks.splice(Math.min(idx, app.data.tasks.length), 0, t);
        if (wasActive && !t.done) setActive(t.id);
        save();
        focusRowAfterUndo(t.id);
      },
    },
  });
}

function addTask(e?: Event) {
  e?.preventDefault();
  const v = input.value.trim();
  if (!v) return input.focus();
  const t: Task = {
    id: uid(),
    text: v,
    notes: '',
    est: estPick,
    done: false,
    count: 0,
    createdAt: Date.now(),
  };
  app.data.tasks.unshift(t);
  trackTask('added');
  if (!app.data.activeTaskId) setActive(t.id);
  input.value = '';
  fx.tap();
  save();
  input.focus();
}

/* --- drag-to-reorder with FLIP animation (pointer: mouse + touch) --- */
function attachGrip(li: HTMLLIElement, t: Task) {
  const grip = li.querySelector<HTMLButtonElement>('.t-grip')!;
  grip.addEventListener('pointerdown', (e) => {
    if (e.button) return;
    e.preventDefault();
    e.stopPropagation();
    startDrag(e, li);
  });
  grip.addEventListener('click', (ev) => ev.stopPropagation());
  grip.addEventListener('keydown', (ev) => {
    if (ev.key !== 'ArrowUp' && ev.key !== 'ArrowDown') return;
    ev.preventDefault();
    const idx = tasks().findIndex((x) => x.id === t.id);
    const ni = ev.key === 'ArrowUp' ? idx - 1 : idx + 1;
    if (ni < 0 || ni >= tasks().length) return;
    app.data.tasks = move(tasks(), idx, ni);
    fx.tick();
    save();
    list.children[ni]?.querySelector<HTMLElement>('.t-grip')?.focus();
    announce(`Moved to position ${ni + 1} of ${tasks().length}`);
  });
}

function startDrag(e: PointerEvent, li: HTMLLIElement) {
  // the list scrolls inside the panel, or inside the Desk layout's side panel
  const pane = list.closest<HTMLElement>('.panes, .side-panel') ?? $('.panes');
  let startY = e.clientY;
  let moved = false;
  let lastY = e.clientY;
  li.classList.add('lift');
  document.body.classList.add('taskdrag');
  try {
    li.setPointerCapture(e.pointerId);
  } catch {
    /* ignore */
  }
  const onMove = (ev: PointerEvent) => {
    lastY = ev.clientY;
    if (Math.abs(ev.clientY - startY) > 3) moved = true;
    li.style.transform = `translateY(${ev.clientY - startY}px)`;
    // swap whenever the card's centre crosses a neighbour's midpoint (handles fast moves)
    for (let guard = 0; guard < 24; guard++) {
      const sibs = [...list.children] as HTMLElement[];
      const liIdx = sibs.indexOf(li);
      const lr = li.getBoundingClientRect();
      const center = lr.top + lr.height / 2;
      let target: HTMLElement | null = null;
      let after = false;
      for (let i = 0; i < sibs.length; i++) {
        if (i === liIdx) continue;
        const sr = sibs[i]!.getBoundingClientRect();
        const mid = sr.top + sr.height / 2;
        if (i > liIdx && center > mid) {
          target = sibs[i]!;
          after = true;
          break;
        }
        if (i < liIdx && center < mid) {
          target = sibs[i]!;
          break;
        }
      }
      if (!target) break;
      const pre = new Map(sibs.map((el) => [el, el.getBoundingClientRect().top]));
      list.insertBefore(li, after ? target.nextSibling : target);
      startY += li.getBoundingClientRect().top - pre.get(li)!;
      li.style.transform = `translateY(${ev.clientY - startY}px)`;
      fx.tick();
      for (const el of sibs) {
        if (el === li) continue;
        const dy = pre.get(el)! - el.getBoundingClientRect().top;
        if (dy)
          animate(el, [{ transform: `translateY(${dy}px)` }, { transform: 'none' }], {
            duration: 230,
            easing: 'cubic-bezier(.32,.72,.24,1)',
          });
      }
    }
  };
  const auto = setInterval(() => {
    const pr = pane.getBoundingClientRect();
    if (lastY < pr.top + 60) pane.scrollTop -= 9;
    else if (lastY > pr.bottom - 60) pane.scrollTop += 9;
  }, 16);
  let ended = false;
  const end = () => {
    if (ended) return; // pointerup is followed by lostpointercapture
    ended = true;
    dragging = false;
    clearInterval(auto);
    li.removeEventListener('pointermove', onMove);
    li.removeEventListener('lostpointercapture', end);
    window.removeEventListener('pointerup', end, true);
    window.removeEventListener('pointercancel', end, true);
    li.classList.remove('lift');
    document.body.classList.remove('taskdrag');
    if (!moved) {
      if (renderPending) renderTasks();
      renderPending = false;
      return;
    }
    renderPending = false;
    suppressRowClick = true;
    setTimeout(() => (suppressRowClick = false), 60);
    const cur = li.style.transform;
    li.style.transform = '';
    if (cur)
      animate(li, [{ transform: cur }, { transform: 'none' }], {
        duration: 190,
        easing: 'cubic-bezier(.32,.72,.24,1)',
      });
    const order = [...list.children].map((el) => (el as HTMLElement).dataset.id);
    const byId = new Map(tasks().map((x) => [x.id, x]));
    const sorted = order.map((id) => byId.get(id!)).filter((x): x is Task => !!x);
    // Tasks added in another tab during the drag have no row yet: keep them, at the end.
    app.data.tasks = [...sorted, ...tasks().filter((x) => !sorted.includes(x))];
    setTimeout(save, 200);
  };
  dragging = true;
  li.addEventListener('pointermove', onMove);
  li.addEventListener('lostpointercapture', end);
  // On the window, so the drag still ends if the pointer is released outside the row.
  window.addEventListener('pointerup', end, true);
  window.addEventListener('pointercancel', end, true);
}

export function initTasks(closeSheet: () => void) {
  $('#taskForm').addEventListener('submit', addTask);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !input.value) {
      input.blur();
      closeSheet();
    }
  });
  const est = (d: number) => () => {
    const v = clamp(estPick + d, CLAMPS.est[0], CLAMPS.est[1]);
    if (v !== estPick) {
      estPick = v;
      $('#estVal').textContent = String(estPick);
      fx.tick();
    }
  };
  holdable($('#estMinus'), est(-1));
  holdable($('#estPlus'), est(1));
  $('#clearDone').addEventListener('click', () => {
    const removed = tasks().filter((t) => t.done);
    app.data.tasks = tasks().filter((t) => !t.done);
    fx.tap();
    save();
    input.focus(); // the button just hid itself
    toast(`Cleared ${removed.length} done task${removed.length === 1 ? '' : 's'}`, {
      action: {
        label: 'UNDO',
        run: () => {
          app.data.tasks.push(...removed.filter((t) => !taskById(t.id)));
          save();
          if (removed[0]) focusRowAfterUndo(removed[0].id);
        },
      },
    });
  });
  on('tasks', renderTasks);
  on('settings', renderTasks); // ETA depends on focus length
  renderTasks();
}
